'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Keeps a step's primary action reachable.
 *
 * The wizard's action rows sit at the natural end of a tall step, which puts them
 * 150–850px below the fold on every viewport smaller than 1920x1080 — and
 * `handleAdvance` scrolls back to the top on each step change, so a visitor lands
 * on a long page with the only way forward off-screen. This docks the row to the
 * bottom of the viewport whenever its natural position is not fully visible, and
 * hands back to the in-flow row once you scroll down to it.
 *
 * Uses `position: fixed`, NOT `position: sticky`: `body { overflow-x: hidden }` in
 * globals.css computes `overflow-y: auto`, which makes body the scrollport and
 * silently breaks sticky for viewport-relative pinning (measured, not assumed).
 * No ancestor of the wizard has a transform/filter/perspective, so `fixed` resolves
 * against the viewport as intended.
 */
export function BookingActionBar({
  children,
  inFlowMarginTop = 32,
}: {
  children: React.ReactNode;
  /** Spacing applied only to the in-flow row, so the docked bar stays compact. */
  inFlowMarginTop?: number;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [docked, setDocked] = useState(false);

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;

    // Fully visible -> render in flow. Otherwise dock it.
    const io = new IntersectionObserver(
      ([entry]) => setDocked(!entry.isIntersecting),
      { threshold: 1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* In-flow row. Keeps its layout space while docked so nothing jumps;
          visibility:hidden also removes it from tab order and the a11y tree,
          so the docked copy is the only reachable control. */}
      <div
        ref={anchorRef}
        style={{ visibility: docked ? 'hidden' : 'visible', marginTop: inFlowMarginTop }}
        aria-hidden={docked || undefined}
      >
        {children}
      </div>

      {docked && (
        <div
          className="booking-action-dock"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 40,
            padding: '14px 20px calc(14px + env(safe-area-inset-bottom, 0px))',
            background:
              'linear-gradient(to top, var(--bg-primary) 55%, color-mix(in srgb, var(--bg-primary) 82%, transparent) 100%)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderTop: '1px solid var(--border)',
          }}
        >
          <div className="mx-auto" style={{ maxWidth: 1180 }}>
            {children}
          </div>
        </div>
      )}
    </>
  );
}
